import { useMemo, useReducer } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import type { SiteDevice, SiteDeviceStatus } from "@/api/schemas/sites";

function statusBadge(status: SiteDeviceStatus) {
  if (status === "online") {
    return (
      <Badge variant="ok" size="sm">
        Online
      </Badge>
    );
  }
  if (status === "alerting") {
    return (
      <Badge variant="warn" size="sm">
        Alerting
      </Badge>
    );
  }
  if (status === "offline") {
    return (
      <Badge variant="err" size="sm">
        Offline
      </Badge>
    );
  }
  return (
    <Badge variant="neutral" size="sm">
      Dormant
    </Badge>
  );
}

const STATUS_RANK: Record<SiteDeviceStatus, number> = {
  dormant: 0,
  offline: 1,
  alerting: 2,
  online: 3,
};

type DeviceFilterState = {
  nameFilter: string;
  macPrefixFilter: string;
  modelFilter: string;
  statusFilter: string;
  lanIpFilter: string;
  publicIpFilter: string;
  addressFilter: string;
  tagFilter: string;
  withoutAddressOnly: boolean;
  lastSeenFilter: string;
};

function buildDeviceColumns({
  nameFilter,
  macPrefixFilter,
  modelFilter,
  statusFilter,
  lanIpFilter,
  publicIpFilter,
  addressFilter,
  tagFilter,
  lastSeenFilter,
  setFilters,
  timeAgo,
}: {
  nameFilter: string;
  macPrefixFilter: string;
  modelFilter: string;
  statusFilter: string;
  lanIpFilter: string;
  publicIpFilter: string;
  addressFilter: string;
  tagFilter: string;
  lastSeenFilter: string;
  setFilters: (p: Partial<DeviceFilterState>) => void;
  timeAgo: (iso: string | null) => string;
}): DataTableColumn<SiteDevice>[] {
  return [
    {
      key: "name",
      label: (
        <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
          <span className="font-mono uppercase tracking-[0.1em]">Name</span>
          <Input
            value={nameFilter}
            onChange={(e) => setFilters({ nameFilter: e.target.value })}
            placeholder="Name or serial"
            className="h-8"
          />
        </div>
      ),
      width: "1.4fr",
      sortable: true,
      sortValue: (r) => r.name,
      render: (r) => (
        <div>
          <div className="truncate text-sm font-semibold text-fg">
            {r.name === "(unnamed)" ? r.serial : r.name}
          </div>
          <div className="font-mono text-[10.5px] text-fg-mute">{r.serial}</div>
        </div>
      ),
    },
    {
      key: "model",
      label: (
        <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
          <span className="font-mono uppercase tracking-[0.1em]">Model</span>
          <Input
            value={modelFilter}
            onChange={(e) => setFilters({ modelFilter: e.target.value })}
            placeholder="Model contains"
            className="h-8"
          />
        </div>
      ),
      width: "120px",
      sortable: true,
      sortValue: (r) => r.model,
      render: (r) => (
        <span className="font-mono text-xs text-fg-dim">{r.model}</span>
      ),
    },
    {
      key: "macAddress",
      label: (
        <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
          <span className="font-mono uppercase tracking-[0.1em]">MAC</span>
          <Input
            value={macPrefixFilter}
            onChange={(e) => setFilters({ macPrefixFilter: e.target.value })}
            placeholder="MAC contains"
            className="h-8"
          />
        </div>
      ),
      width: "150px",
      render: (r) => (
        <span className="font-mono text-xs text-fg-dim">
          {r.macAddress ?? "-"}
        </span>
      ),
    },
    {
      key: "status",
      label: (
        <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
          <span className="font-mono uppercase tracking-[0.1em]">Status</span>
          <Input
            value={statusFilter}
            onChange={(e) => setFilters({ statusFilter: e.target.value })}
            placeholder="Contains"
            className="h-8"
          />
        </div>
      ),
      width: "120px",
      sortable: true,
      sortValue: (r) => STATUS_RANK[r.status],
      render: (r) => statusBadge(r.status),
    },
    {
      key: "lanIp",
      label: (
        <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
          <span className="font-mono uppercase tracking-[0.1em]">LAN IP</span>
          <Input
            value={lanIpFilter}
            onChange={(e) => setFilters({ lanIpFilter: e.target.value })}
            placeholder="Contains"
            className="h-8"
          />
        </div>
      ),
      width: "150px",
      render: (r) => (
        <span className="font-mono text-xs text-fg-dim">{r.lanIp ?? "-"}</span>
      ),
    },
    {
      key: "publicIp",
      label: (
        <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
          <span className="font-mono uppercase tracking-[0.1em]">
            Public IP
          </span>
          <Input
            value={publicIpFilter}
            onChange={(e) => setFilters({ publicIpFilter: e.target.value })}
            placeholder="Contains"
            className="h-8"
          />
        </div>
      ),
      width: "150px",
      render: (r) => (
        <span className="font-mono text-xs text-fg-dim">
          {r.publicIp ?? "-"}
        </span>
      ),
    },
    {
      key: "address",
      label: (
        <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
          <span className="font-mono uppercase tracking-[0.1em]">Address</span>
          <Input
            value={addressFilter}
            onChange={(e) => setFilters({ addressFilter: e.target.value })}
            placeholder="Contains"
            className="h-8"
          />
        </div>
      ),
      width: "1.2fr",
      render: (r) => (
        <span className="text-xs text-fg-dim">{r.address ?? "-"}</span>
      ),
    },
    {
      key: "tags",
      label: (
        <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
          <span className="font-mono uppercase tracking-[0.1em]">Tags</span>
          <Input
            value={tagFilter}
            onChange={(e) => setFilters({ tagFilter: e.target.value })}
            placeholder="Contains"
            className="h-8"
          />
        </div>
      ),
      width: "1fr",
      render: (r) => (
        <span className="font-mono text-xs text-fg-dim">
          {(r.tags ?? []).join(", ") || "-"}
        </span>
      ),
    },
    {
      key: "lastSeen",
      label: (
        <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
          <span className="font-mono uppercase tracking-[0.1em]">
            Last seen
          </span>
          <Input
            value={lastSeenFilter}
            onChange={(e) => setFilters({ lastSeenFilter: e.target.value })}
            placeholder="Contains"
            className="h-8"
          />
        </div>
      ),
      width: "150px",
      sortable: true,
      sortValue: (r) => r.lastSeen ?? "",
      render: (r) => (
        <span className="text-xs text-fg-dim">{timeAgo(r.lastSeen)}</span>
      ),
    },
  ];
}

export function DevicesTable({
  devices,
  embedded,
  enableExport = true,
  timeAgo,
}: {
  devices: SiteDevice[];
  embedded?: boolean;
  enableExport?: boolean;
  timeAgo: (iso: string | null) => string;
}) {
  const [
    { nameFilter, macPrefixFilter, modelFilter, statusFilter, lanIpFilter, publicIpFilter, addressFilter, tagFilter, withoutAddressOnly, lastSeenFilter },
    setFilters,
  ] = useReducer(
    (s: DeviceFilterState, p: Partial<DeviceFilterState>) => ({ ...s, ...p }),
    { nameFilter: "", macPrefixFilter: "", modelFilter: "", statusFilter: "", lanIpFilter: "", publicIpFilter: "", addressFilter: "", tagFilter: "", withoutAddressOnly: false, lastSeenFilter: "" },
  );

  const filtered = useMemo(() => {
    const nameQ = nameFilter.trim().toLowerCase();
    const macPrefixQ = macPrefixFilter.trim().toLowerCase();
    const modelQ = modelFilter.trim().toLowerCase();
    const statusQ = statusFilter.trim().toLowerCase();
    const lanIpQ = lanIpFilter.trim().toLowerCase();
    const publicIpQ = publicIpFilter.trim().toLowerCase();
    const addressQ = addressFilter.trim().toLowerCase();
    const tagQ = tagFilter.trim().toLowerCase();
    const lastSeenQ = lastSeenFilter.trim().toLowerCase();

    return devices.filter((device) => {
      if (
        nameQ &&
        !(
          device.name.toLowerCase().includes(nameQ) ||
          device.serial.toLowerCase().includes(nameQ)
        )
      ) {
        return false;
      }
      if (
        macPrefixQ &&
        !(device.macAddress ?? "").toLowerCase().includes(macPrefixQ)
      ) {
        return false;
      }
      if (modelQ && !device.model.toLowerCase().includes(modelQ)) return false;
      if (statusQ && !device.status.toLowerCase().includes(statusQ))
        return false;
      if (lanIpQ && !(device.lanIp ?? "").toLowerCase().includes(lanIpQ)) {
        return false;
      }
      const hasAddress = Boolean((device.address ?? "").trim());
      if (withoutAddressOnly && hasAddress) {
        return false;
      }
      if (
        publicIpQ &&
        !(device.publicIp ?? "").toLowerCase().includes(publicIpQ)
      ) {
        return false;
      }
      if (
        addressQ &&
        !(device.address ?? "").toLowerCase().includes(addressQ)
      ) {
        return false;
      }
      if (
        tagQ &&
        !(device.tags ?? []).some((tag) => tag.toLowerCase().includes(tagQ))
      ) {
        return false;
      }
      if (
        lastSeenQ &&
        !timeAgo(device.lastSeen).toLowerCase().includes(lastSeenQ) &&
        !(device.lastSeen ?? "").toLowerCase().includes(lastSeenQ)
      ) {
        return false;
      }
      return true;
    });
  }, [
    devices,
    lanIpFilter,
    lastSeenFilter,
    macPrefixFilter,
    modelFilter,
    nameFilter,
    addressFilter,
    withoutAddressOnly,
    publicIpFilter,
    tagFilter,
    statusFilter,
    timeAgo,
  ]);

  const cols = buildDeviceColumns({
    nameFilter,
    macPrefixFilter,
    modelFilter,
    statusFilter,
    lanIpFilter,
    publicIpFilter,
    addressFilter,
    tagFilter,
    lastSeenFilter,
    setFilters,
    timeAgo,
  });

  const content = (
    <DataTable
      columns={cols}
      data={filtered}
      rowKey={(r) => r.serial}
      showTotal={false}
      enableExport={enableExport}
      exportFileName="network-devices"
      toolbar={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <span className="font-mono text-[11px] text-fg-mute">
            {filtered.length} / {devices.length} devices
          </span>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant={!withoutAddressOnly ? "primary" : "ghost"}
              onClick={() => setFilters({ withoutAddressOnly: false })}
            >
              All
            </Button>
            <Button
              size="sm"
              variant={withoutAddressOnly ? "primary" : "ghost"}
              onClick={() => setFilters({ withoutAddressOnly: true })}
            >
              Without address
            </Button>
          </div>
        </div>
      }
    />
  );

  if (embedded) return content;

  return (
    <Card>
      <CardContent>{content}</CardContent>
    </Card>
  );
}
